/**
 * Sanitizes a OneNote page's raw content HTML before it is written into the
 * export (ARCHITECTURE.md §8: "content is HTML; images need separate
 * authenticated fetch").
 *
 * The same security boundary as Teams' sanitize-html.ts, for the same
 * reason: a page's HTML can be authored by anyone with edit access to a
 * shared notebook, not just the exporter's own account, and it is about to
 * become a local HTML file opened directly in a browser. Allowlist, not
 * blocklist — an unrecognised tag is dropped with its subtree.
 *
 * One addition Teams' sanitizer does not need: OneNote pages position
 * content with inline `style="position:absolute;left:...;top:..."` on divs —
 * that is how OneNote's freeform canvas layout actually works, and dropping
 * `style` outright would flatten every page into an unreadable stack of
 * paragraphs. So `style` is kept on allowed elements, but with any `url(...)`
 * function stripped out first: a CSS `background: url(https://evil/track.gif)`
 * would otherwise be a way for page content to make an outbound network
 * request the moment the exported file is opened, which is exactly what
 * "no user content ever leaves the browser" (ARCHITECTURE.md §1) rules out —
 * that guarantee is about outbound calls, not just script execution.
 */

const ALLOWED_TAGS = new Set([
    "p",
    "br",
    "b",
    "strong",
    "i",
    "em",
    "u",
    "s",
    "span",
    "div",
    "a",
    "ul",
    "ol",
    "li",
    "blockquote",
    "code",
    "pre",
    "table",
    "thead",
    "tbody",
    "tr",
    "td",
    "th",
    "img",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6"
]);

/** Elements OneNote positions with inline style — kept, with url()/expression() stripped. */
const STYLE_ALLOWED_TAGS = new Set(["div", "span", "p", "td", "th", "table"]);

const SAFE_URL_SCHEMES = new Set(["http:", "https:", "mailto:"]);

function isSafeLinkHref(href: string): string | null {
    try {
        const url = new URL(href, "https://invalid.example/");
        return SAFE_URL_SCHEMES.has(url.protocol) ? url.toString() : null;
    } catch {
        return null;
    }
}

/** Strips CSS functions that can trigger a network fetch or (in legacy engines) execute code. */
function sanitizeStyle(style: string): string {
    return style.replace(/url\s*\([^)]*\)/gi, "").replace(/expression\s*\([^)]*\)/gi, "");
}

/**
 * Every `<img src>` in the page, in the exact string form the DOM reports —
 * used both to fetch each image and as the sanitizer's lookup key, so the two
 * passes must agree on the same string.
 */
export function extractImageSrcs(contentHtml: string): string[] {
    const doc = new DOMParser().parseFromString(contentHtml, "text/html");
    const seen = new Set<string>();
    for (const img of Array.from(doc.querySelectorAll("img"))) {
        const src = img.getAttribute("src");
        if (src) {
            seen.add(src);
        }
    }
    return [...seen];
}

function sanitizeElement(el: Element, out: Node, assetUrlMap: ReadonlyMap<string, string>): void {
    const tag = el.tagName.toLowerCase();

    if (!ALLOWED_TAGS.has(tag)) {
        return;
    }

    if (tag === "img") {
        const originalSrc = el.getAttribute("src");
        const localSrc = originalSrc ? assetUrlMap.get(originalSrc) : undefined;
        if (!localSrc) {
            return;
        }
        const img = document.createElement("img");
        img.setAttribute("src", localSrc);
        const alt = el.getAttribute("alt");
        if (alt) {
            img.setAttribute("alt", alt);
        }
        out.appendChild(img);
        return;
    }

    const clean = document.createElement(tag);

    if (tag === "a") {
        const href = el.getAttribute("href");
        const safe = href ? isSafeLinkHref(href) : null;
        if (safe) {
            clean.setAttribute("href", safe);
            clean.setAttribute("rel", "noopener noreferrer");
        }
    }

    if (STYLE_ALLOWED_TAGS.has(tag)) {
        const style = el.getAttribute("style");
        if (style) {
            clean.setAttribute("style", sanitizeStyle(style));
        }
    }

    for (const child of Array.from(el.childNodes)) {
        sanitizeNode(child, clean, assetUrlMap);
    }

    out.appendChild(clean);
}

function sanitizeNode(node: Node, out: Node, assetUrlMap: ReadonlyMap<string, string>): void {
    if (node.nodeType === Node.TEXT_NODE) {
        out.appendChild(document.createTextNode(node.textContent ?? ""));
        return;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
        sanitizeElement(node as Element, out, assetUrlMap);
    }
}

export interface SanitizedPage {
    title: string;
    bodyHtml: string;
}

/**
 * Parses a full OneNote page document (Graph returns a complete
 * `<html><head><title>...</title></head><body>...</body></html>`, not a
 * fragment the way Teams message bodies are) and returns a safe title plus
 * sanitized body HTML.
 */
export function sanitizePage(
    contentHtml: string,
    assetUrlMap: ReadonlyMap<string, string>
): SanitizedPage {
    const doc = new DOMParser().parseFromString(contentHtml, "text/html");
    const title = doc.querySelector("title")?.textContent?.trim() || "Untitled page";

    const out = document.createDocumentFragment();
    for (const child of Array.from(doc.body?.childNodes ?? [])) {
        sanitizeNode(child, out, assetUrlMap);
    }

    const wrapper = document.createElement("div");
    wrapper.appendChild(out);
    return { title, bodyHtml: wrapper.innerHTML };
}
